// Scheduling and opening-hours events invalidate API state; they never supply
// an outlet's effective policy directly. Parent scheduling changes affect all
// outlets, including outlets with their own opening hours.
export const VENDOR_AVAILABILITY_EVENTS = [
  "vendor:availabilityChanged",
  "brandHoursUpdated",
  "restaurantHoursUpdated",
  "healthcareHoursUpdated",
  "retailerHoursUpdated",
];
const legacyTypes = {
  brandHoursUpdated: "brand", restaurantHoursUpdated: "restaurant",
  healthcareHoursUpdated: "healthcare", retailerHoursUpdated: "retailer",
};
const typeOf = (value) => {
  const type = String(value || "").toLowerCase().replace(/[^a-z]/g, "");
  if (["brand", "brands", "brandowner", "publication"].includes(type)) return "brand";
  if (["restaurant", "restaurants", "food"].includes(type)) return "restaurant";
  if (["healthcare", "pharmacy", "health"].includes(type)) return "healthcare";
  if (["retailer", "retail", "wholesaler", "wholesale", "retailerwholesaler", "rw"].includes(type)) return "retailer";
  return null;
};
const ids = (...values) => values.filter(Boolean).map(String);

export function matchesVendorAvailability(query, payload = {}, event = "vendor:availabilityChanged") {
  payload = payload || {};
  const type = typeOf(payload.vendorType || legacyTypes[event]);
  if (!type || type !== typeOf(query?.vendorType)) return false;
  const changedOutlet = payload.outletId || (payload.isOutlet
    ? payload.restaurantId || payload.healthcareId || payload.vendorId || payload.brandId
    : null);
  if (changedOutlet) {
    return ids(query?.outletId, query?.vendorId).includes(String(changedOutlet));
  }
  const changed = ids(payload.vendorId, payload.ownerId, payload.brandId,
    payload.restaurantId, payload.healthcareId, payload.retailerId,
    ...(Array.isArray(payload.outletIds) ? payload.outletIds : []));
  if (!changed.length) return false;
  // An outlet-only query has no parent identifier to compare. Re-read that
  // outlet on a parent event of the same type rather than miss inheritance.
  if (!query?.vendorId && query?.outletId) return true;
  return ids(query?.vendorId, query?.outletId).some((id) => changed.includes(id));
}

// Coalesce canonical + legacy events from one save (and reconnect bursts).
export function subscribeVendorAvailability(socket, query, refresh) {
  if (!socket?.on || !query?.vendorType) return () => {};
  let timer;
  const queueRefresh = () => {
    if (timer) return;
    timer = setTimeout(() => { timer = null; refresh(); }, 75);
  };
  const handlers = VENDOR_AVAILABILITY_EVENTS.map((event) => {
    const handle = (payload) => {
      if (matchesVendorAvailability(query, payload, event)) queueRefresh();
    };
    socket.on(event, handle);
    return [event, handle];
  });
  // socket.io emits connect on every reconnection, including after network
  // outages. Re-read what may have changed while this client was disconnected.
  socket.on("connect", queueRefresh);
  return () => {
    clearTimeout(timer);
    handlers.forEach(([event, handle]) => socket.off(event, handle));
    socket.off("connect", queueRefresh);
  };
}
